import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Card, Cell, Image, Spacer, Text } from '@vinted/web-ui';
import { Screen } from 'constants/tracking/screens';
import { HC_HOME_VISIBLE_TRANSACTION_COUNT, HC_ROOT_FAQ_ID } from 'constants/help-center';
import { FaqEntryType } from 'constants/faq-entry';
import { TrackingChannel } from 'constants/tracking/tracking-channels';
import HelpCenterSearch from 'components/HelpCenterSearch';
import HelpCenterNavigation from 'components/HelpCenterNavigation';
import ContentLoader from 'components/ContentLoader';
import useFetch from 'hooks/useFetch';
import useIsDarkMode from 'hooks/useIsDarkMode';
import useLocation from 'hooks/useLocation';
import useTracking from 'hooks/useTracking';
import useTranslate from 'hooks/useTranslate';
import { getRecentTransactions, getFaqEntries, getFaqEntryByType } from 'data/api';
import { transformHelpCenterHomeFaqsResponse, transformHelpCenterTransactionsResponse, transformHelpCenterNavigationResponse } from 'data/transformers/help-center';
import { transformFaqEntryResponse } from 'data/api/transformers/response';
import { generateFaqLink } from 'libs/utils/faq';
import { viewFaqEntry } from 'libs/common/event-tracker/events';
import { getIsFeatureSwitchEnabled } from 'state/feature-switches/selectors';
import { getIsAuthenticated, getIsWebView } from 'state/session/selectors';
import { STATIC_ASSETS_URL } from 'constants/index';
import HelpCenterTransactions from '../HelpCenterTransactions';
import useClientSideOnly from '../../../../next/hooks/useClientSideOnly';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

const HelpCenterHome = () => {
  // Prevents translations rendering before spinner is shown
  const isClientSideRenderReady = useClientSideOnly();
  const translate = useTranslate('help_center');
  const {
    track
  } = useTracking();
  const isDarkMode = useIsDarkMode();
  const isLoggedIn = useSelector(getIsAuthenticated);
  const {
    access_channel,
    thread_id
  } = useLocation().searchParams;
  const isFeedbackFormEnabled = useSelector(getIsFeatureSwitchEnabled('product_feedback_form'));
  const isWebView = useSelector(getIsWebView);
  useEffect(() => {
    track(viewFaqEntry({
      faqEntryId: HC_ROOT_FAQ_ID,
      faqEntryChannel: TrackingChannel.HelpCenter,
      faqAccessChannel: typeof access_channel === 'string' ? access_channel : null,
      screen: Screen.HelpRoot
    }));
  }, [access_channel, track]);
  const {
    fetch: fetchRecentTransactions,
    transformedData: recentTransactions,
    isLoading: isTransactionsLoading
  } = useFetch(getRecentTransactions, transformHelpCenterTransactionsResponse);
  const {
    fetch: fetchFaqs,
    data: faqs,
    transformedData: faqEntries,
    isLoading: isFaqsLoading
  } = useFetch(getFaqEntries, transformHelpCenterHomeFaqsResponse);
  const {
    fetch: fetchFaqEntryByType,
    transformedData: feedbackFaqEntry,
    isLoading: isFeedbackFaqEntryLoading
  } = useFetch(getFaqEntryByType, transformFaqEntryResponse);
  useEffect(() => {
    Promise.all([fetchRecentTransactions(HC_HOME_VISIBLE_TRANSACTION_COUNT), fetchFaqs(), fetchFaqEntryByType(FaqEntryType.MembersFeedback)]);
  }, [fetchFaqs, fetchRecentTransactions, fetchFaqEntryByType]);

  const renderSearch = () => {
    if (isWebView) return null;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(HelpCenterSearch, {}), /*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.Medium
      })]
    });
  };

  const renderNavigation = () => {
    if (!faqs) return null;
    const navigationFaqEntries = transformHelpCenterNavigationResponse(faqs);
    return /*#__PURE__*/_jsx(HelpCenterNavigation, {
      navigationFaqEntries: navigationFaqEntries,
      isLoading: isFaqsLoading
    });
  };

  const renderTransactions = () => {
    if (!(recentTransactions !== null && recentTransactions !== void 0 && recentTransactions.transactions.length)) return null;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Cell, {
        children: [/*#__PURE__*/_jsx(Text, {
          type: Text.Type.Heading,
          width: Text.Width.Parent,
          children: translate('transactions.title')
        }), /*#__PURE__*/_jsx(Text, {
          type: Text.Type.Subtitle,
          children: translate('transactions.subtitle')
        })]
      }), /*#__PURE__*/_jsx(HelpCenterTransactions, {
        transactions: recentTransactions.transactions,
        hasMoreTransactions: recentTransactions.totalCount > HC_HOME_VISIBLE_TRANSACTION_COUNT
      })]
    });
  };

  const renderIcon = (icon, title) => {
    if (!icon) return null;
    return /*#__PURE__*/_jsx(Image, {
      src: "".concat(STATIC_ASSETS_URL, "/assets/icons/faq/").concat(icon).concat(isDarkMode ? '_dark' : '', ".svg"),
      size: Image.Size.Large,
      alt: title
    });
  };

  const renderEntry = _ref => {
    let {
      id,
      title,
      icon
    } = _ref;
    if (!title) return null;
    return /*#__PURE__*/_jsxs("a", {
      href: generateFaqLink({
        id,
        title
      }, access_channel, thread_id),
      className: "faq__category",
      title: title,
      "data-testid": "help-center-home--faq-category-".concat(id),
      children: [renderIcon(icon, title), /*#__PURE__*/_jsx("div", {
        className: "faq__category-title",
        children: title
      })]
    }, id);
  };

  const renderFeedbackFormLink = () => {
    if (!isFeedbackFormEnabled || !isLoggedIn) {
      return null;
    }

    if (isFeedbackFaqEntryLoading || !feedbackFaqEntry) {
      return /*#__PURE__*/_jsx(ContentLoader, {});
    }

    const feedbackFaqEntryUrl = generateFaqLink({
      id: feedbackFaqEntry.id,
      title: feedbackFaqEntry.title
    }, access_channel, thread_id);
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.Medium
      }), /*#__PURE__*/_jsx(Cell, {
        testId: "faq-feedback-entry",
        url: feedbackFaqEntryUrl,
        type: Cell.Type.Navigating,
        title: translate('faq.give_us_feedback'),
        chevron: true
      })]
    });
  };

  const renderFaqEntries = () => {
    if (!faqEntries) return null;
    return /*#__PURE__*/_jsx(Card, {
      children: /*#__PURE__*/_jsx("div", {
        className: "faq__categories",
        role: "list",
        children: faqEntries.map(renderEntry)
      })
    });
  };

  if (isTransactionsLoading || isFaqsLoading || !isClientSideRenderReady) {
    return /*#__PURE__*/_jsx(ContentLoader, {
      styling: ContentLoader.Styling.Wide,
      size: ContentLoader.Size.Large,
      testId: "help-center-home-loader"
    });
  }

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("aside", {
      className: "body-content__sidebar faq__navigation faq__navigation--secondary",
      children: renderNavigation()
    }), /*#__PURE__*/_jsxs("div", {
      className: "faq",
      children: [renderSearch(), renderTransactions(), /*#__PURE__*/_jsx(Cell, {
        children: /*#__PURE__*/_jsx(Text, {
          as: "h2",
          type: Text.Type.Heading,
          text: translate('faq.general_topics')
        })
      }), renderFaqEntries(), renderFeedbackFormLink()]
    })]
  });
};

export default HelpCenterHome;